import type { BookingConfirmationSummary } from '@massilia/queries'

import {
  useDownloadBookingSessionPaymentReceiptPdfMutation,
  useDownloadBookingSessionReservationContractPdfMutation,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { CarRentalsSection } from '@/components/booking-sections/car-rentals-section'
import { ExtrasSection } from '@/components/booking-sections/extras-section'
import { HotelsSection } from '@/components/booking-sections/hotels-section'
import { InsuranceSection } from '@/components/booking-sections/insurance-section'
import { SpecificitySection } from '@/components/specificity/specificity-section'

import type { PaymentConfirmationBookingData } from './payment-confirmation-booking-data'

import { CompleteYourTripSection } from './complete-your-trip-section'
import { PaymentConfirmationLayout } from './payment-confirmation-layout'
import { PaymentConfirmationSharedScreen } from './payment-confirmation-shared-screen'
import { paymentConfirmationVariants } from './payment-confirmation-variants'
import { PaymentReceiptActions } from './payment-receipt-actions'
import { SummaryRow } from './summary-row'
import { TripRecapSection } from './trip-recap-section'

interface PaymentCompleteComponentProps {
  sessionId?: string
  booking: PaymentConfirmationBookingData
  summary?: BookingConfirmationSummary
}

export function PaymentCompleteComponent({
  sessionId,
  booking,
  summary,
}: PaymentCompleteComponentProps) {
  const variant = paymentConfirmationVariants.success
  const downloadContractMutation = useDownloadBookingSessionReservationContractPdfMutation()
  const downloadReceiptMutation = useDownloadBookingSessionPaymentReceiptPdfMutation()
  const latestPaidPaymentId = summary?.latestPaidPaymentId ?? null

  const handleDownloadContract = async () => {
    if (!sessionId) return
    try {
      const blob = await downloadContractMutation.mutateAsync(sessionId)
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch {
      toast.error('Erreur lors du téléchargement du contrat')
    }
  }

  const handleDownloadReceipt = async () => {
    if (!sessionId || !latestPaidPaymentId) {
      throw new Error('missing session or payment')
    }
    return downloadReceiptMutation.mutateAsync({
      paymentId: latestPaidPaymentId,
      sessionId,
    })
  }

  return (
    <PaymentConfirmationLayout variant={variant}>
      <div className="border-gray-light-200 flex flex-col gap-3 rounded-2xl border bg-white p-6">
        <div className="text-gray-light-900 text-2xl font-semibold">{variant.title}</div>
        <p className="text-gray-light-700 text-sm">{variant.description}</p>

        <SummaryRow label="Numéro de dossier" value={booking.bookingRef} />
        <SummaryRow label="Adresse email" value={booking.customerEmail} />
        <SummaryRow label="Téléphone" value={booking.customerPhone} />
        <SummaryRow label="Tarif" value={booking.totalAmountLabel} />

        <div className="mt-2 flex flex-wrap gap-3">
          <Link to="/login">
            <Button type="button">Accéder à ma réservation</Button>
          </Link>
          {sessionId && (
            <Button onClick={handleDownloadContract} type="button" variant="outline">
              {downloadContractMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <FileText className="size-4" />
              )}
              Télécharger le contrat
            </Button>
          )}
          {sessionId && latestPaidPaymentId && (
            <PaymentReceiptActions
              isLoading={downloadReceiptMutation.isPending}
              onDownload={handleDownloadReceipt}
            />
          )}
        </div>
      </div>

      <TripRecapSection summary={summary} />

      {summary?.insurance && <InsuranceSection insurance={summary.insurance} />}
      {summary?.hotels && summary.hotels.length > 0 && <HotelsSection hotels={summary.hotels} />}
      {summary?.carRentals && summary.carRentals.length > 0 && (
        <CarRentalsSection carRentals={summary.carRentals} />
      )}
      {summary?.extras && summary.extras.length > 0 && <ExtrasSection extras={summary.extras} />}

      <CompleteYourTripSection
        bookingId={summary?.bookingId}
        hasInsurance={!!summary?.insurance}
        sessionId={sessionId}
        summary={summary}
      />

      {sessionId && <SpecificitySection sessionId={sessionId} />}

      <PaymentConfirmationSharedScreen
        departureAirportCode={summary?.flightSegments[0]?.departureLocation}
      />
    </PaymentConfirmationLayout>
  )
}
